import { Link, useLocation } from "react-router-dom";
import {
  calculateDurationObject,
  extractDomainFromEmail,
  extractNameFromEmail,
  formatDurationFromSeconds,
  validateDate,
} from "../../utils/utils";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import logo7G from "../../assets/common/logo7G.svg";
const initialSort = { sort_field: "start_time", sort_order: "desc" };

const getColumns = (employee_email_ids: string) => {
  const email = employee_email_ids;
  const name = extractNameFromEmail("",email );
  const domain = extractDomainFromEmail(email);
  const headers = {
    userTalkTime: `${name} Talk Time`,
    AccountTalkTime: `${domain} Talk Time`,
  };
  return [
    {
      accessorKey: "title",
      header: "Interaction title",
      Cell: ({ cell, row }: { cell: any; row: any }) => {
        const attendees = row.original.attendees;
        let allEmails: any[] = [];
        if (attendees && Object.keys(attendees).length > 0) {
          for (const key in attendees) {
            allEmails = allEmails.concat(attendees[key]);
          }
        }
        const isExternal = row.original.is_external;
        const theme = useTheme();
        const location = useLocation();
        const is_team_meeting_id=location.state?.is_team_meeting_id ?? false;
        const clientDomains = row?.original?.clientDomains;
        const endTime = row?.original?.end_time;
        const isMeetingOver = endTime && new Date(endTime) < new Date();
        return (
          <Box className="flex flex-row gap-1 justify-between items-start w-full">
            <Tooltip
              title={
                <>
                  {`${cell.getValue() as string} : (${
                    row.original.no_of_attendees
                  } invitees)`}
                  <br />
                  {clientDomains && clientDomains.length > 0 && (
                    <>
                      Clients:{" "}
                      {clientDomains.map((domain: string, index: number) => (
                        <span key={index}>{domain.split(".")[0]} {index < clientDomains.length - 1 && ", "}</span>
                      ))}
                    </>
                  )}
                </>
              }
              placement="right"
            >
              <Link
                color="primary"
                to={`/meeting/${row.id}?${(() => {
                  const params = new URLSearchParams();
                  // only store meaningful values
                  if (row.original?.selectedTab) {
                    params.set("st", row.original.selectedTab);
                  }
                  if (is_team_meeting_id === true || row.original?.isTeamMeeting) {
                    params.set("ti", "1");
                  }
                  if (isMeetingOver) {
                    params.set("imo", "1");
                  }
                
                  return params.toString();
                })()}`}
                state={{
                  is_team_meeting_id: is_team_meeting_id === true ? true : row.original.isTeamMeeting ?? false,
                  selectedTab: row.original.selectedTab, 
                  startEndTime: row.original.startEndTime, 
                  newUserTeamData: row.original.newUserTeamData,
                  parent_page_identifier: row.original.parent_page_identifier,
                  ParentPageIdentifier: row.original.parent_page_identifier,
                  isMeetingOver: isMeetingOver,
                }}
                style={{
                  flexGrow: 0,
                  flexShrink: 1,
                  flexBasis: "auto",
                  textDecoration: "underline",
                  textDecorationColor: "rgba(25, 118, 210, 0.4)",
                  color: "#1976d2",
                  whiteSpace: "nowrap",
                }}
                className="truncate-table-cell-ellipsis"
              >
                   {(cell.getValue() as string)}
              </Link>
            </Tooltip>
            <Box
              className="flex flex-row justify-center items-center gap-2"
              sx={{
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: "auto",
                minWidth: "70px",
                maxWidth: "70px",
                display: "inline-block",
                textAlign: "center",
                background: "#ffffff",
                borderRadius: "16px",
                border: `1px ${
                  isExternal ? theme.palette.brand.main : "#585858"
                }  solid`,
                padding: "4px 8px",
                [theme.breakpoints.down("lg")]: {
                  padding: "2px 6px",
                },
                marginRight:"40px",
                marginLeft:"auto"
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: isExternal ? theme.palette.brand.main : "#222222",
                  fontWeight: "600",
                  wordWrap: "break-word",
                }}
              >
                {isExternal ? "External" : "Internal"}
              </Typography>
            </Box>
          </Box>
        );
      },
      enableColumnFilter: false,
      grow: true,
      minSize: 280,
      size: 280,
    },
    {
      accessorKey: "start_time",
      header: "Date",
      filterVariant: "date",
      filterFn: "lessThan",
      sortingFn: "datetime",
      Cell: ({ cell }: { cell: any }) => {
        return (
            <div>
            <span>{validateDate(cell.getValue(), "MMM DD, YYYY hh:mm A")}</span>
            </div>
        );
      },
      grow: true,
      minSize: 150,
      size: 150,
    },
    {
      accessorKey: "meeting_sentiment",
      header: "Rating",
      Cell: ({ cell, row }: { cell: any; row: any }) => {
        const sentiment = cell.getValue();
        const startTime = new Date(row.original.start_time);
        const currentTime = new Date();
        const [timeLeft, setTimeLeft] = React.useState(() => calculateTimeLeft(startTime));
        const duration = row.original.duration;
        const endTime = new Date(startTime.getTime() + duration * 60000);
        const botStatus = row.original.botStatus;

        React.useEffect(() => {
          const interval = setInterval(() => {
            setTimeLeft(calculateTimeLeft(startTime));
          }, 60000); // Update every minute
          return () => clearInterval(interval);
        }, [startTime]);
        function calculateTimeLeft(startTime: Date) {
          const currentTime = new Date();
          const timeDiff = startTime.getTime() - currentTime.getTime();
          const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
          return {
            days: Math.max(days, 0),
            hours: Math.max(hours, 0),
            minutes: Math.max(minutes, 0),
          };
        }
        if (startTime > currentTime) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: "#B902CA" }}>
              <AccessTimeIcon sx={{ fontSize: "15px" }} />
                <Typography sx={{ fontSize: "14px" }}>
                {timeLeft.days > 0
                  ? `Starts in ${timeLeft.days}d ${timeLeft.hours}h ${timeLeft.minutes}m`
                  : `Starts in ${timeLeft.hours}h ${timeLeft.minutes}m`}
                </Typography>
                {botStatus==="Scheduled" && (
                  <Tooltip title="Bot Scheduled" arrow>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", position: "relative" }}>
                    <img
                      src={logo7G}
                      alt="7thGear"
                      style={{
                        width: '50px',
                        height: '15px',
                        top: '2px',
                        left: '5px',
                        marginLeft: '-10px',
                      }}
                    />
                  </Box>
                  </Tooltip>
              )}
            </Box>
          );
        }

        if (currentTime >= startTime && currentTime <= endTime) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: "#B902CA" }}>
              <AccessTimeIcon sx={{ fontSize: "15px" }} />
                <Typography sx={{ fontSize: "14px" }}>
                In Progress
                </Typography>
            </Box>
          );
        }
          let color;

          // If sentiment is null, return "No recording" or any fallback text
          if (sentiment === null) {
              return <Typography sx={{ fontSize: "13px" }}>No recording</Typography>;
          }
  
          // Set color based on sentiment
          switch (sentiment) {
              case "Positive":
                  color = "#04BD00"; // Green
                  break;
              case "Negative":
                  color = "#F4442F"; // Red
                  break;
              case "Neutral":
                  color = "#FFCD2A"; // Orange for neutral
                  break;
              default:
                  return <Typography>No recording</Typography>; // Default for unexpected values
          }
  
          return (
              <Box
                  sx={{
                    width: 14,        // Increased circle size
                    height: 14,
                    backgroundColor: color,
                    borderRadius: "3px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: "15px",
                    textAlign: "center",  // Ensure content inside the circle is centered
                    lineHeight: "24px", // Make sure the text is vertically centered
                  }}
                  title={sentiment}
              />
          );
      },
      enableSorting: false,
      grow: true,
      minSize: 100,
      size: 100,
  },
  {
    accessorKey: "duration",
    header: "Planned Duration",
    enableColumnFilter: false,
    Cell: ({ cell }: { cell: any }) => {
      const timeSpend = calculateDurationObject(
        cell.getValue() as number
      ) || {
        days: 0,
        hours: 0,
        minutes: 0,
      };
      return (
        <span>
          {timeSpend.days > 0 &&
            `${timeSpend.days} ${timeSpend.days === 1 ? "day" : "days"}`}
          {timeSpend.hours > 0 &&
            ` ${timeSpend.hours} ${timeSpend.hours === 1 ? "hr" : "hrs"}`}
          {timeSpend.minutes > 0 && ` ${timeSpend.minutes} mins`}
        </span>
      );
    },
    grow: true,
    minSize: 120,
    size: 120
  },
  {
    accessorKey: "actualDuration",
    header: "Actual Duration",
    meta: {
      customHeader: (
        <span
          title="Actual Duration"
          style={{
            textOverflow: 'ellipsis',
            width: '100%',
          }}
        >
          Actual Duration
        </span>
      )
    },
      Cell: ({ cell }: { cell: any }) => {
      const duration = cell.getValue();
      if (duration == null) return <span>—</span>;
      return (
        <span>
          {duration >= 60
            ? formatDurationFromSeconds(duration).replace(/\d+s/, '').trim()
            : formatDurationFromSeconds(duration)}
        </span>
      );
    },
    enableColumnFilter: false,
    grow: true,
    minSize: 130,
    size: 130,
  },
  {
    accessorKey: "selfTalkTime",
    header: headers.userTalkTime,
    Header: () => (
        <Box
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            "&:hover": {
              overflow: "visible",
            },
          }}
        >
          {headers.userTalkTime}
        </Box>
    ),
      Cell: ({ cell, row }: { cell: any, row: any }) => {
      const selfTalkTime = cell.getValue();
      const actualDuration = row.original.actualDuration;
      if (selfTalkTime == null) {
        return <span>—</span>;
      }
      const shouldShowPercentage = actualDuration && actualDuration > 0;
      const percentage = ((selfTalkTime / actualDuration ) * 100).toFixed(1);
      return (
        <span>
          {selfTalkTime >= 60
            ? formatDurationFromSeconds(selfTalkTime).replace(/\d+s/, '').trim()
            : formatDurationFromSeconds(selfTalkTime)}
          {shouldShowPercentage && (
          <Typography component="span" sx={{ fontSize: "12px" }}>
            {" "}({percentage}%)
          </Typography>
          )}
        </span>
      );
    },
    enableColumnFilter: false,
    size: 150,
    minSize: 150,
  },
  {
    accessorKey: "accountTalkTime",
    header: headers.AccountTalkTime,
    Header: () => (
      <Box
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          "&:hover": {
            overflow: "visible",
          },
        }}
      >
        {headers.AccountTalkTime}
      </Box>
  ),
      Cell: ({ cell, row }: { cell: any, row: any }) => {
      const accountTalkTime = cell.getValue();
      const actualDuration = row.original.actualDuration;
      if (accountTalkTime == null) {
        return <span>—</span>;
      }
      const shouldShowPercentage = actualDuration && actualDuration > 0;
      const percentage = ((accountTalkTime / actualDuration ) * 100).toFixed(1);
      return (
        <span>
          {accountTalkTime >= 60
            ? formatDurationFromSeconds(accountTalkTime).replace(/\d+s/, '').trim()
            : formatDurationFromSeconds(accountTalkTime)}
          {shouldShowPercentage && (
          <Typography component="span" sx={{ fontSize: "12px" }}>
            {" "}({percentage}%)
          </Typography>
          )}
        </span>
      );
    },
    enableColumnFilter: false,
    size: 150,
    minSize: 150,
  },
  {
    accessorKey: "clientTalkTime",
    header: "Client Talk Time",
    Cell: ({ cell, row }: { cell: any, row: any }) => {
     const clientTalkTime = cell.getValue();
      const actualDuration = row.original.actualDuration;
      if (clientTalkTime == null) {
        return <span>—</span>;
      }
      const shouldShowPercentage = actualDuration && actualDuration > 0;
      const percentage = ((clientTalkTime / actualDuration ) * 100).toFixed(1);
      return (
        <span>
          {clientTalkTime >= 60
            ? formatDurationFromSeconds(clientTalkTime).replace(/\d+s/, '').trim()
            : formatDurationFromSeconds(clientTalkTime)}
          {shouldShowPercentage && (
          <Typography component="span" sx={{ fontSize: "12px"}}>
            {" "}({percentage}%)
          </Typography>
          )}
        </span>
      );
    },
    enableColumnFilter: false,
    grow: true,
    minSize: 130,
    size: 130,
  },
  ];
};

export { getColumns, initialSort };
